'use client';

import { Box, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { AbgTypography } from '@abg/kit-base';

import { sanitizeHtml } from '../../shared/utils';

import { IHTMLEmbedProps } from './html-embed.types';
import {
  containerStyling,
  contentStyling,
  contentWrapperStyling,
  footerStyling,
} from './HtmlEmbed.styling';
import { appendToBody, appendToHead } from './utils';

export const HTMLEmbed = ({ aemAttributes, model }: IHTMLEmbedProps) => {
  const shouldAppendToHead = model.head;

  const scriptContainerReference = useRef<HTMLDivElement>(null);
  const [isInnerHtmlReady, setIsInnerHtmlReady] = useState(false);

  useEffect(() => {
    if (!model.scriptContent) return;

    if (shouldAppendToHead) {
      return appendToHead(model.scriptContent);
    }

    if (isInnerHtmlReady) {
      return appendToBody(
        model.scriptContent,
        scriptContainerReference.current,
      );
    }

    return;
  }, [model.scriptContent, shouldAppendToHead, isInnerHtmlReady]);

  useEffect(() => {
    if (shouldAppendToHead) return;

    const temporaryDiv = document.createElement('div');
    temporaryDiv.innerHTML = model.scriptContent || '';

    // Remove scripts from inner HTML when appending to body
    for (const s of temporaryDiv.querySelectorAll('script')) s.remove();

    if (scriptContainerReference.current) {
      scriptContainerReference.current.innerHTML = temporaryDiv.innerHTML;
    }

    setIsInnerHtmlReady(true);
  }, [model.scriptContent, shouldAppendToHead]);

  return (
    <Box {...aemAttributes} data-aue-type="component" sx={containerStyling}>
      {!shouldAppendToHead && (
        <Stack sx={contentWrapperStyling}>
          {model.title && (
            <AbgTypography variant="h4" data-testid="embed-component-title">
              {model.title}
            </AbgTypography>
          )}

          <Stack sx={contentStyling}>
            <div
              data-testid="embed-component-script"
              ref={scriptContainerReference}
            />

            {model.footerText && (
              <AbgTypography
                variant="bodySmallRegular"
                component="div"
                sx={footerStyling}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(model.footerText),
                }}
                data-testid="embed-component-footer"
              />
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
